import { isAxiosError } from 'axios';
import { SERVER_ERROR_CODES } from '@/consts/server-error-codes.consts';

const CONTENT_POLICY_VIOLATION_CODE = 1068;
const CUSTOM_EFFECT_PAID_PLAN_REQUIRED_CODE = 1135;

interface ApiErrorResponse {
  internalErrorCode?: number;
  message?: string;
}

/** A model-run failure whose application-owned message is translated at the UI boundary. */
export class ModelRunError extends Error {
  constructor(
    public readonly kind: 'failed' | 'canceled' | 'timeout' | 'missing_prediction',
    public readonly predictionId?: string,
    public readonly providerMessage?: string,
    options?: ErrorOptions,
  ) {
    super(providerMessage ?? kind, options);
    this.name = 'ModelRunError';
  }
}

export const getContentModerationError = (e: unknown): string | null => {
  if (!isAxiosError<ApiErrorResponse>(e)) {
    return null;
  }
  const data = e.response?.data;
  if (data?.internalErrorCode === CONTENT_POLICY_VIOLATION_CODE) {
    return data.message ?? null;
  }
  return null;
};

/** Workspace out of credits (1019, 1052) and member over their monthly cap (1065). */
const NOT_ENOUGH_CREDITS_CODES: ReadonlySet<number> = new Set([1019, 1052, 1065]);

/** The server refused a run because the balance can't pay for it. */
export const isNotEnoughCreditsError = (e: unknown): boolean => {
  const code = isAxiosError<ApiErrorResponse>(e) ? e.response?.data?.internalErrorCode : undefined;
  return code !== undefined && NOT_ENOUGH_CREDITS_CODES.has(code);
};

export const isCustomEffectPaidPlanError = (e: unknown): boolean =>
  isAxiosError<ApiErrorResponse>(e) && e.response?.data?.internalErrorCode === CUSTOM_EFFECT_PAID_PLAN_REQUIRED_CODE;

/** Duplicate/copy of FigmaNode snapshots requires a linked Figma account (HTTP 403, code 1114). */
export const isFigmaNodeNotConnectedError = (e: unknown): boolean =>
  isAxiosError<ApiErrorResponse>(e) &&
  e.response?.data?.internalErrorCode === SERVER_ERROR_CODES.FIGMA_NODE_NOT_CONNECTED;

/** A move into My Files refused because the caller does not own everything being moved (HTTP 403, code 1211). */
export const isMoveToPersonalNotOwnerError = (e: unknown): boolean =>
  isAxiosError<ApiErrorResponse>(e) &&
  e.response?.data?.internalErrorCode === SERVER_ERROR_CODES.MOVE_TO_PERSONAL_NOT_OWNER;

/** The server's message for a publish refused over deprecated or unsupported models; it names the models. */
export const getToolPublishUnavailableModelsError = (e: unknown): string | null => {
  if (!isAxiosError<ApiErrorResponse>(e)) {
    return null;
  }
  const data = e.response?.data;
  if (data?.internalErrorCode === SERVER_ERROR_CODES.TOOL_PUBLISH_UNAVAILABLE_MODELS) {
    return data.message ?? null;
  }
  return null;
};
